import {
  Button,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Input,
  ModalFooter,
  ModalBody,
  ModalHeader,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { FiPlusCircle } from "react-icons/fi";
import { AdminsTable } from "./AdminsTable";
import actions from "../actions";
import { IKYDUser } from "@common/interfaces/KYDUser";
import PhoneInput from "react-phone-input-2";
import parsePhoneNumber from "libphonenumber-js";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useDialog } from "../Common/Dialog";

enum EditOrganizationModalType {
  INACTIVE,
  ADD_ADMIN,
  DELETE_ADMIN_CONFIRM,
}

const App = ({ currentUser }: { currentUser: CognitoUser | null }) => {
  const [admins, setAdmins] = useState<IKYDUser[] | null>(null);
  const [loading, setLoading] = useState<any>({});
  const [modalType, setModalType] = useState(
    EditOrganizationModalType.INACTIVE
  );
  const [dirtyText, setDirtyText] = useState<any>({});
  const toast = useToast();
  const dialog = useDialog();

  const updateDirtyText = useCallback((key: string, value: string) => {
    if (key === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        return { ...l, [key]: value };
      });
    }
  }, []);

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const renderAddAdminModal = () => (
    <Modal
      size={"xl"}
      isOpen={true}
      onClose={() => setModalType(EditOrganizationModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Admin</ModalHeader>
        <ModalBody>
          <Stack w="100%" spacing={4}>
            <HStack w="100%">
              <Stack w="50%">
                <Text fontWeight={"medium"}>First Name</Text>
                <HStack w="100%">
                  <Input
                    maxWidth="100%"
                    isDisabled={loading["add_admin"]}
                    onChange={(e) => {
                      updateDirtyText("first_name", e.target.value);
                    }}
                    value={dirtyText[`first_name`]}
                  />
                </HStack>
              </Stack>
              <Stack w="50%">
                <Text fontWeight={"medium"}>Last Name</Text>
                <HStack>
                  <Input
                    w="100%"
                    isDisabled={loading["add_admin"]}
                    onChange={(e) => {
                      updateDirtyText("last_name", e.target.value);
                    }}
                    value={dirtyText[`last_name`]}
                  />
                </HStack>
              </Stack>
            </HStack>
            <Stack w="100%">
              <Text fontWeight={"medium"}>Email</Text>
              <HStack>
                <Input
                  w="100%"
                  isDisabled={loading["add_admin"]}
                  onChange={(e) => {
                    updateDirtyText("email", e.target.value);
                  }}
                  value={dirtyText[`email`]}
                />
              </HStack>
            </Stack>
            <Stack w="100%">
              <Text fontWeight={"medium"}>Phone Number</Text>
              <HStack>
                <PhoneInput
                  disabled={loading["add_admin"]}
                  autoFormat={true}
                  inputProps={{
                    placeholder: "Mobile number",
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "60px",
                    fontFamily: "Work sans",
                    borderWidth: "1px",
                    borderRadius: "5px",
                  }}
                  countryCodeEditable={false}
                  country={"us"}
                  value={dirtyText[`phone_number`]}
                  onChange={(value: string) => {
                    updateDirtyText("phone_number", value);
                  }}
                />
              </HStack>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["add_admin"]}
            onClick={() => {
              setModalType(EditOrganizationModalType.INACTIVE);
              updateDirtyText("all", "");
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={loading["add_admin"]}
            onClick={() => onAddAdmin(dirtyText)}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const fetchAdmins = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        updateLoading("init", true);
      }
      let admins: IKYDUser[] = [];
      try {
        const _ = await actions.ADMIN.fetchOrganization();
        if (_ && _.admins) {
          setAdmins(_.admins as IKYDUser[]);
          admins = _.admins as IKYDUser[];
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return admins;
    },
    [updateLoading, dialog]
  );

  const sleep = (milliseconds: number) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };

  useEffect(() => {
    fetchAdmins();
  }, [updateLoading, fetchAdmins]);

  const onAddAdmin = useCallback(
    async (params: any) => {
      updateLoading("add_admin", true);
      try {
        const { first_name, last_name, email, phone_number } = params;

        const parsedPhoneNumber = parsePhoneNumber(`+${phone_number}`);
        if (!parsedPhoneNumber || !parsedPhoneNumber.number) {
          throw Error("Invalid phone number format");
        }

        const { user_id, did_exist } = await actions.ADMIN.updateOrganization(
          "add_admin",
          {
            first_name,
            last_name,
            email,
            phone_number: parsedPhoneNumber.number,
          }
        );

        if (!did_exist) {
          let found_admin = false;
          let attempts = 0;
          do {
            const a = await fetchAdmins(true);
            if (a) {
              found_admin = !!a.find((a: IKYDUser) => a.id === user_id);
            }
            attempts += 1;
            await sleep(3000);
          } while (!found_admin && attempts < 5);
        } else {
          await fetchAdmins(true);
        }

        toast({
          title: "Admin Added",
          description: `"${first_name}" has been added`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        updateDirtyText("all", "");
        updateLoading("add_admin", false);
        setModalType(EditOrganizationModalType.INACTIVE);
      } catch (err) {
        dialog({ text: err.message });
        updateLoading("add_admin", false);
      }
    },
    [fetchAdmins, dialog]
  );

  const onRemoveAdmin = useCallback(
    async (userId: string) => {
      updateLoading("remove_admin", true);
      updateLoading(userId, true);
      try {
        await actions.ADMIN.updateOrganization("remove_admin", {
          user_id: userId,
        });
        await fetchAdmins(true);
        toast({
          title: "Admin Removed",
          description: `Admin has been removed`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        updateDirtyText("all", "");
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("remove_admin", false);
      updateLoading(userId, false);
      setModalType(EditOrganizationModalType.INACTIVE);
    },
    [fetchAdmins, dialog]
  );

  return (
    <Stack spacing={{ base: "8", lg: "6" }}>
      {modalType === EditOrganizationModalType.ADD_ADMIN &&
        renderAddAdminModal()}
      <Stack
        spacing="4"
        direction={{ base: "column", lg: "row" }}
        justify="space-between"
        align={{ base: "start", lg: "center" }}
      >
        <Stack spacing="1">
          <Heading
            size={useBreakpointValue({ base: "xs", lg: "sm" })}
            fontWeight="medium"
          >
            <HStack>
              <Text fontSize={"lg"} fontWeight="bold">
                Admins
              </Text>
              <IconButton
                onClick={() =>
                  setModalType(EditOrganizationModalType.ADD_ADMIN)
                }
                icon={<FiPlusCircle fontSize="1.25rem" />}
                variant="ghost"
                aria-label="Add admin"
              />
            </HStack>
          </Heading>
        </Stack>
      </Stack>

      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading admins...</Text>
        </VStack>
      )}
      {admins && (
        <Stack overflowX={"scroll"} spacing={{ base: "5", lg: "6" }}>
          <AdminsTable
            currentUser={currentUser}
            loading={loading}
            onRemoveAdmin={onRemoveAdmin}
            admins={admins}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default App;
